import { z } from 'zod';
import { currencySchema, refSchema } from '../../common';
import { nameSchema, objectIdSchema } from '../../validation';

/**
 * Inter-Sales reference shapes.
 *
 * Cross-document references in the `sales/` domain are stored as embedded
 * snapshots — never bare ObjectIds. This mirrors the canonical
 * `partySchema` pattern already used by `Invoice.payer`, `Order.contracts[].buyer`,
 * `Agreement.parties[]`, `Vehicle.owner`, etc.
 *
 * Two reasons:
 *
 * 1. **Audit & display.** A Billing or BillingTransaction must read
 *    meaningfully without a join: "Acme Logistics · Growth Plan · March 2026"
 *    should be visible from the row itself. The Org/Subscription/Billing
 *    doc can be deleted, renamed, archived, or migrated and the historical
 *    record stays intact.
 *
 * 2. **Codebase precedent.** All cross-doc refs in the codebase already
 *    follow this convention. See `libs/shared/schema/src/common/party.schema.ts`.
 *
 * For Org references, use `partySchema` from `common/`. For Subscription
 * and Billing references (which are NOT Parties), use these minimal
 * snapshot shapes.
 */

/**
 * Snapshot reference to a `Subscription` document. Captured at write-time
 * on `Billing` and `BillingTransaction` and never auto-refreshed.
 *
 * The `planCode` / `interval` / `currency` fields anchor the historical
 * display so a Billing row can show "Growth · Monthly · USD" without
 * loading the live Subscription.
 */
export const subscriptionRefSchema = z.object({
  /** Subscription._id */
  _id: objectIdSchema,
  /** mirrored from Subscription.ref (e.g. SUB-2026-000123) */
  ref: refSchema.optional(),
  /** the plan at the time of snapshot — display anchor */
  planCode: z.string(),
  /** the interval at the time of snapshot */
  interval: z.enum(['month', 'semester', 'year']),
  /** the currency at the time of snapshot (frozen on the subscription) */
  currency: currencySchema,
});
export type SubscriptionRef = z.infer<typeof subscriptionRefSchema>;

/**
 * Snapshot reference to a `Billing` document. Captured at write-time on
 * `BillingTransaction` (and on credit / refund Billings via `parentBilling`)
 * and never auto-refreshed.
 */
export const billingRefSchema = z.object({
  /** Billing._id */
  _id: objectIdSchema,
  /** mirrored from Billing.ref (e.g. BIL-2026-03-000045) */
  ref: refSchema.optional(),
  /** mirrored from Billing.title (e.g. "Growth Plan — March 2026") */
  title: nameSchema,
});
export type BillingRef = z.infer<typeof billingRefSchema>;
